import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getModelsForMake, type ModelWithCount } from "@/lib/vehicle-queries";
import { getBrandProfile } from "@/lib/brand-maintenance.functions";
import {
  brandFaqs,
  brandSummary,
  buildBrandSchedule,
  isElectricBrand,
  isLuxuryBrand,
  oilInterval,
  serviceCostBand,
  type BrandProfile,
} from "@/lib/brand-maintenance";
import { breadcrumbLd, faqLd, itemListLd, pageTitle, SITE_OG_IMAGE } from "@/lib/seo";
import { BlufSummary } from "@/components/BlufSummary";
import { EstimatedBadge } from "@/components/EstimatedBadge";
import { FaqBlock } from "@/components/FaqBlock";
import { MaintenanceSpecLink } from "@/components/MaintenanceSpecLink";
import { maintenanceSpecMakeUrl } from "@/lib/maintenance-spec";
import { isIndexableBrandPage, NOINDEX_META } from "@/lib/index-policy";
import { isMaintenanceDepthBrand } from "@/lib/depth-scope";
import { buildMaintenanceDepth } from "@/lib/maintenance-depth";

export const Route = createFileRoute("/$make/maintenance-schedule")({
  loader: async ({ params }) => {
    const [result, profile] = await Promise.all([
      getModelsForMake({ data: { makeSlug: params.make } }),
      getBrandProfile({ data: { makeSlug: params.make } }),
    ]);
    if (!result.make || result.models.length === 0 || !profile) throw notFound();
    return { ...result, profile };
  },
  component: BrandMaintenancePage,
  head: ({ loaderData, params }) => {
    if (!loaderData?.make || !loaderData.profile) {
      return {
        meta: [{ title: "Not found — AutoSpecData" }, { name: "robots", content: "noindex" }],
      };
    }
    const profile = loaderData.profile as BrandProfile;
    const makeName = loaderData.make.name;
    const path = `https://autospecdata.com/${params.make}/maintenance-schedule`;
    const title = pageTitle(`${makeName} Maintenance Schedule by Mileage`);
    const cost = serviceCostBand(profile);
    const description = isElectricBrand(profile)
      ? `${makeName} maintenance schedule: no oil changes, tire rotation every 7,500 miles, brake fluid, and battery cooling service — with real costs (about $${cost.low}-$${cost.high} a visit).`
      : `${makeName} maintenance schedule: ${oilInterval(profile).toLocaleString()}-mile oil interval, brake, transmission and coolant services by mileage, plus what each one costs (about $${cost.low}-$${cost.high} for routine work).`;
    const indexable = isIndexableBrandPage(params.make, profile.modelCount, profile.rowCount);
    return {
      meta: [
        { title },
        ...(indexable ? [] : NOINDEX_META),
        { name: "description", content: description },
        { property: "og:title", content: `${makeName} Maintenance Schedule` },
        { property: "og:description", content: description },
        { property: "og:url", content: path },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: makeName, path: `/${params.make}` },
              { name: "Maintenance Schedule", path },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqLd(brandFaqs(profile))),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            itemListLd(
              `${makeName} model maintenance schedules`,
              loaderData.models.slice(0, 40).map((m: ModelWithCount) => ({
                name: `${makeName} ${m.name} maintenance schedule`,
                path: `https://autospecdata.com/${params.make}/${m.slug}/maintenance-schedule`,
              })),
            ),
          ),
        },
      ],
    };
  },
});

function BrandMaintenancePage() {
  const { make, models, profile } = Route.useLoaderData();
  const p = profile as BrandProfile;
  const schedule = buildBrandSchedule(p);
  const depth = isMaintenanceDepthBrand(p.makeSlug) ? buildMaintenanceDepth(p) : [];
  const electric = isElectricBrand(p);
  const cost = serviceCostBand(p);
  const faqs = brandFaqs(p);

  return (
    <div>
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/$make" params={{ make: make!.slug }} className="hover:text-foreground">
          {make!.name}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Maintenance Schedule</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            {make!.name} Maintenance Schedule — Service Intervals by Mileage
          </h1>
          <div className="mt-3"><EstimatedBadge /></div>
          <BlufSummary text={brandSummary(p)} />
          <Link to="/car-maintenance-schedule" className="cta-gold mt-6">
            Full maintenance checklist by mileage
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="grid sm:grid-cols-4 gap-3">
          <Stat
            label={electric ? "Oil changes" : "Oil interval"}
            value={electric ? "None" : `${oilInterval(p).toLocaleString()} mi`}
          />
          <Stat label="Routine service" value={`$${cost.low}-$${cost.high}`} />
          <Stat label="Models covered" value={String(p.modelCount)} />
          <Stat
            label="Model years"
            value={p.yearMin && p.yearMax ? `${p.yearMin}-${p.yearMax}` : "—"}
          />
        </div>
      </section>

      {depth.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
            <div className="space-y-5 max-w-3xl">
              {depth.map((d) => (
                <div key={d.heading}>
                  <h2 className="text-base font-semibold text-foreground">{d.heading}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{d.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <h2 className="text-xl font-bold text-foreground mb-2">
          {make!.name} service intervals and what each one actually catches
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {electric
            ? `Built from the ${p.rowCount.toLocaleString()} ${make!.name} configurations in our database — all of them electric, so the schedule below drops every combustion service.`
            : `Built from the ${p.rowCount.toLocaleString()} ${make!.name} configurations in our database, averaging ${(p.avgCylinders ?? 4).toFixed(1)} cylinders${p.hybridShare > 0.1 ? ` with ${Math.round(p.hybridShare * 100)}% hybrids` : ""}${p.dieselShare > 0.03 ? ` and ${Math.round(p.dieselShare * 100)}% diesels` : ""}.`}
        </p>
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="spec-table">
            <thead>
              <tr>
                <th>Mileage</th>
                <th>Service</th>
                <th>Shop note</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((row) => (
                <tr key={row.miles}>
                  <td data-numeric className="font-semibold text-primary">
                    {row.miles.toLocaleString()} mi
                  </td>
                  <td>{row.service}</td>
                  <td>{row.shopNote}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Intervals reflect the {make!.name} powertrain mix in our data set. Your owner's manual is
          the authority — follow its severe-duty column if you tow, idle, or drive mostly short
          trips.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
        <h2 className="text-lg font-semibold text-foreground mb-3">
          What makes {make!.name} upkeep different
        </h2>
        <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
          {electric ? (
            <li>
              No oil, filters, plugs or belts. Tires, brake fluid and the high-voltage cooling loop
              are the entire cost of ownership on the maintenance side.
            </li>
          ) : (
            <li>
              {make!.name} engines here average {(p.avgCylinders ?? 4).toFixed(1)} cylinders, which
              is why the oil interval lands at {oilInterval(p).toLocaleString()} miles rather than a
              generic 5,000.
            </li>
          )}
          {p.hybridShare > 0.1 && (
            <li>
              {Math.round(p.hybridShare * 100)}% of the lineup is hybrid: brake pads last longer,
              but the battery cooling fan needs cleaning that a gas car never does.
            </li>
          )}
          {p.evShare > 0.03 && !electric && (
            <li>
              {Math.round(p.evShare * 100)}% of {make!.name} configurations are fully electric — on
              those, skip every oil, plug and belt line in the table above.
            </li>
          )}
          {p.dieselShare > 0.03 && (
            <li>
              {Math.round(p.dieselShare * 100)}% run diesel, adding fuel filters, DEF and DPF
              regeneration to the schedule.
            </li>
          )}
          {isLuxuryBrand(make!.slug) && (
            <li>
              Parts and specified fluids sit at the premium end, so a dealer quote typically runs
              30-50% above the ${cost.low}-${cost.high} independent-shop band.
            </li>
          )}
          {p.topClasses.length > 0 && (
            <li>
              Most common body types in the data: {p.topClasses.join(", ")} — heavier classes wear
              brakes and tires faster than the interval table implies.
            </li>
          )}
        </ul>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {make!.name} maintenance schedules by model
        </h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {models.map((m: ModelWithCount) => (
            <Link
              key={m.id}
              to="/$make/$model/$dataType"
              params={{ make: make!.slug, model: m.slug, dataType: "maintenance-schedule" }}
              className="rounded-lg border border-border bg-card px-4 py-3 text-sm hover:border-primary hover:text-primary transition-colors"
            >
              {make!.name} {m.name} service intervals
            </Link>
          ))}
        </div>
      </section>

      {maintenanceSpecMakeUrl(make!.slug) && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-8">
          <MaintenanceSpecLink
            heading={`${make!.name} maintenance schedules`}
            body="For manufacturer-based service intervals and maintenance schedules by model, see MaintenanceSpec."
            anchor={`${make!.name} maintenance schedules`}
            href={maintenanceSpecMakeUrl(make!.slug)!}
          />
        </section>
      )}

      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-12 border-t border-border">
        <h3 className="text-xl font-bold text-foreground mb-4">
          Frequently Asked Questions About {make!.name} Maintenance
        </h3>
        <FaqBlock items={faqs} />
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-bold text-foreground">{value}</div>
    </div>
  );
}
